import api from './api'

const authService = {
  async login(username, password) {
    try {
      // Fazer requisição para o endpoint local de autenticação
      const response = await api.post('/auth/login', {
        username: username,
        password: password
      })

      // Se chegou aqui, o login foi bem-sucedido
      if (response.success) {
        // Salvar o token JWT e dados do usuário
        this.setAuthData({
          token: response.token,
          user: response.user
        })

        return {
          success: true,
          data: response,
          status: 200
        }
      } else {
        return {
          success: false,
          message: response.message || 'Erro no login',
          status: 400
        }
      }

    } catch (error) {
      console.error('Erro no login:', error)
      
      // Verificar se é erro de rede ou erro de autenticação
      if (error.response) {
        // Servidor respondeu com erro (401, 403, 500, etc.)
        return {
          success: false,
          message: (error.response.data && error.response.data.message) || 'Credenciais inválidas',
          status: error.response.status
        }
      } else if (error.request) {
        // Erro de rede/conexão
        return {
          success: false,
          message: 'Erro de conexão. Verifique sua internet e tente novamente.',
          status: 0
        }
      } else {
        // Erro desconhecido
        return {
          success: false,
          message: 'Erro inesperado. Tente novamente.',
          status: 0
        }
      }
    }
  },

  // Método para logout (limpar dados locais e notificar o servidor)
  async logout() {
    try {
      const token = this.getToken()
      
      if (token) {
        // Notificar o servidor sobre o logout
        await api.post('/auth/logout', {})
      }
    } catch (error) {
      console.error('Erro ao fazer logout no servidor:', error)
      // Continuar com logout local mesmo se houver erro no servidor
    }
    
    // Limpar dados locais
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('user')
    localStorage.removeItem('authToken')
    
    // Emitir evento personalizado para notificar sobre mudança de autenticação
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-state-changed', {
        detail: { authenticated: false }
      }));
    }
  },

  // Verificar se o usuário está autenticado
  isAuthenticated() {
    const token = this.getToken()
    const isAuth = localStorage.getItem('isAuthenticated') === 'true'
    return isAuth && token
  },

  // Verificação simples de token (apenas local, sem API)
  hasValidToken() {
    const token = this.getToken()
    if (!token) return false
    
    try {
      // Decodificar o token JWT para verificar se expirou
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Date.now() / 1000
      
      return payload.exp > now
    } catch (error) {
      console.error('Erro ao verificar token:', error)
      return false
    }
  },

  // Obter token JWT
  getToken() {
    return localStorage.getItem('authToken')
  },

  // Obter dados do usuário
  getUser() {
    try {
      const userStr = localStorage.getItem('user')
      return userStr ? JSON.parse(userStr) : null
    } catch (error) {
      console.error('Erro ao obter dados do usuário:', error)
      return null
    }
  },

  // Salvar dados de autenticação
  setAuthData(authData) {
    localStorage.setItem('isAuthenticated', 'true')
    
    if (authData.token) {
      localStorage.setItem('authToken', authData.token)
    }
    
    if (authData.user) {
      localStorage.setItem('user', JSON.stringify(authData.user))
    }
    
    // Emitir evento personalizado para notificar sobre mudança de autenticação
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-state-changed', {
        detail: { authenticated: true }
      }));
    }
  },

  // Verificar se o token está válido (fazer requisição para o servidor)
  async validateToken() {
    try {
      const token = this.getToken()
      
      if (!token) {
        return false
      }

      const response = await api.get('/auth/me')

      if (response.success) {
        // Atualizar dados do usuário se necessário
        this.setAuthData({
          user: response.user
        })
        return true
      }
      
      return false
    } catch (error) {
      console.error('Erro na validação do token:', error)
      
      // Só fazer logout se for erro 401 (token inválido)
      if (error.response && error.response.status === 401) {
        console.warn('Token inválido, fazendo logout...')
        this.logout()
      }
      
      return false
    }
  },

  // Verificar se o usuário é admin
  isAdmin() {
    const user = this.getUser()
    return user && user.role === 'admin'
  },

  // Verificar se o usuário é gestor
  isGestor() {
    const user = this.getUser()
    return user && user.role === 'gestor'
  },

  // Verificar se o usuário é admin ou gestor
  isAdminOrGestor() {
    const user = this.getUser()
    return user && (user.role === 'admin' || user.role === 'gestor')
  },

  // Verificar se o usuário é user
  isUser() {
    const user = this.getUser()
    return user && user.role === 'user'
  },

  // Verificar se o usuário tem uma role específica
  hasRole(role) {
    const user = this.getUser()
    return user && user.role === role
  },

  // Verificar se o usuário tem uma das roles permitidas
  hasAnyRole(roles) {
    const user = this.getUser()
    return user && roles.includes(user.role)
  },

  // Verificar se o usuário pode acessar uma rota
  canAccess(routeName) {
    const user = this.getUser()
    if (!user) return false

    // Definir permissões por rota
    const permissions = {
      'Dashboard': ['user', 'admin', 'gestor'],
      'Home': ['admin', 'gestor'],
      'Cronogramas': ['admin', 'gestor'],
      'Usuarios': ['admin'] // Apenas admin pode acessar gestão de usuários
    }

    const allowedRoles = permissions[routeName]
    return allowedRoles ? allowedRoles.includes(user.role) : false
  }
}

export default authService 